import { createContext, useContext, useMemo, useCallback, type ReactNode } from 'react';
import { callService as haCallService, type Connection } from 'home-assistant-js-websocket';

interface HAConnectionCtx {
  connection: Connection | null;
  callService: (
    domain: string,
    service: string,
    serviceData?: object,
    target?: object,
  ) => Promise<unknown>;
}

const Ctx = createContext<HAConnectionCtx>({
  connection: null,
  callService: async () => {
    throw new Error('HAConnectionProvider missing');
  },
});

export function HAConnectionProvider({
  connection,
  children,
}: {
  connection: Connection | null;
  children: ReactNode;
}): JSX.Element {
  const cs = useCallback(
    (domain: string, service: string, serviceData?: object, target?: object) => {
      if (!connection) {
        console.warn('[ha] callService called without connection', domain, service);
        return Promise.reject(new Error('no connection'));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return haCallService(connection, domain, service, serviceData, target as any);
    },
    [connection],
  );
  const value = useMemo<HAConnectionCtx>(() => ({ connection, callService: cs }), [connection, cs]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useHAService(): HAConnectionCtx {
  return useContext(Ctx);
}
